import type { ReactNode } from 'react'
import type { THeadCellWithActions } from '~/components/table/models'

import { EConversationChannel } from '~/models/features'

export enum EReportDashboard {
  aiAssistant = 'ai-assistant',
  phoneCalls = 'phone-call',
  voipMatchback = 'voip-lead',
  download = 'export-data',
}

export type TReportDashboardOption = {
  key: EReportDashboard
  label: string
  value: EReportDashboard
  sectionTitle: string
  sectionSubtitle: string
}

export const REPORT_DASHBOARD_OPTIONS: TReportDashboardOption[] = [
  {
    key: EReportDashboard.aiAssistant,
    label: 'AI Assistant Reporting',
    value: EReportDashboard.aiAssistant,
    sectionTitle: 'Reports',
    sectionSubtitle:
      'Track conversations, qualification outcomes, and lead performance to understand how your AI Assistant is impacting pipeline and response quality.',
  },
  {
    key: EReportDashboard.phoneCalls,
    label: 'Phone Call Reporting',
    value: EReportDashboard.phoneCalls,
    sectionTitle: 'Call Reporting (Beta)',
    sectionSubtitle:
      'Track call volume, answer rates, missed calls, and voicemail activity across your locations, phone numbers, and call handling types.',
  },
  {
    key: EReportDashboard.voipMatchback,
    label: 'VoIP Lead Response',
    value: EReportDashboard.voipMatchback,
    sectionTitle: 'VoIP Lead Response',
    sectionSubtitle:
      'Group inbound lead phone numbers by outbound call attempts and track lead-response performance across your selected date range.',
  },
  {
    key: EReportDashboard.download,
    label: 'Export data',
    value: EReportDashboard.download,
    sectionTitle: 'Export Your Data',
    sectionSubtitle: 'Download your data and reports from your AI assistant',
  },
]

export enum ECallDirectionFilterValue {
  all = 'all',
  inbound = 'inbound',
  outbound = 'outbound',
}

export enum ECallOriginationFilterValue {
  all = 'all',
  aiAgent = 'aiAgent',
  direct = 'direct',
}

export enum EVoipMatchbackSourceType {
  all = 'all',
  campaignUpload = 'campaign-upload',
  phone = 'phone',
  chat = 'chat',
  upload = 'upload',
  email = 'email',
  web = 'web',
}

export type TVoipMatchbackBucket = {
  outboundCalls: number
  leadCount: number
}

export type TVoipMatchbackLead = {
  contactId: string
  leadPhoneNumber: string
  leadName: string | null
  assistantReferenceName: string | null
  leadLocation: string | null
  leadSource: string | null
  leadCreatedDate: string
  outboundCalls: number
  lastCallDate: string | null
  timeToFirstCallMinutes: number | null
  timeToSecondCallMinutes: number | null
}

export type TVoipMatchbackLocation = {
  locationName: string
  leadCount: number
  neverCalledCount: number
  neverCalledPercentage: number
}

export type TVoipMatchbackData = {
  totals: {
    leads: number
    matchedLeads: number
    unmatchedLeads: number
    outboundCalls: number
  }
  buckets: TVoipMatchbackBucket[]
  locations: TVoipMatchbackLocation[]
  leads: TVoipMatchbackLead[]
}

export type TVoipMatchbackApiResponse = {
  data?: Partial<TVoipMatchbackData>
}

type TCountWithChange = {
  count: number
  previousCount: number | null
  changeFromLastPeriod: number | null
}

type TAnsweredRate = {
  /** Null when there were no inbound direct calls. */
  rate: number | null
  previousRate: number | null
  changeFromLastPeriod: number | null
}

type TMissedCalls = {
  count: number
  previousCount: number | null
  /** Null when there were no inbound direct calls. */
  percentage: number | null
  changeFromLastPeriod: number | null
}

export type TCallsByLocation = {
  locationId: string
  name: string
  city: string | null
  state: string | null
  totalCalls: number
  inboundCalls: number
  inboundAiCalls: number
  inboundDirectCalls: number
  outboundCalls: number
  outboundAiCalls: number
  outboundDirectCalls: number
  aiCalls: number
  directCalls: number
  missedCalls: number
  voicemailCalls: number
  avgCallDurationSeconds: number
  answeredCalls: number
  answeredRate: number | null
}

export type TCallsByAssistant = {
  agentId: string
  name: string
  phoneNumber: string | null
  locationId: string | null
  totalCalls: number
  inboundCalls: number
  inboundAiCalls: number
  inboundDirectCalls: number
  outboundCalls: number
  outboundAiCalls: number
  outboundDirectCalls: number
  aiCalls: number
  directCalls: number
  missedCalls: number
  voicemailCalls: number
  avgCallDurationSeconds: number
  answeredCalls: number
  answeredRate: number | null
}

type TCallVolumePoint = {
  date: string
  inboundAi: number
  outboundAi: number
  inboundDirect: number
  outboundDirect: number
}

export type TCallMetricsOverTimePoint = {
  date: string
  totalCalls: number
  answeredCount: number
  answeredRate: number | null
  missedCount: number
  missedPercentage: number | null
  voicemailCount: number
  inbound: number
  outbound: number
  aiAgent: number
  direct: number
}

type TCallsByHourPoint = {
  hour: number
  totalCalls: number
  answeredCalls: number
  missedCalls: number
}

export type TCallsByDayOfWeekPoint = {
  dayOfWeek: number
  totalCalls: number
  /** Inbound direct call count for the day — the correct answered/missed-rate denominator (voicemail/unknown outcomes are neither answered nor missed). */
  inboundDirectCount: number
  answeredCalls: number
  missedCalls: number
}

type TAnsweredByHourAndDayPoint = {
  hour: number
  mondayAvgAnswered: number
  tuesdayAvgAnswered: number
  wednesdayAvgAnswered: number
  thursdayAvgAnswered: number
  fridayAvgAnswered: number
  saturdayAvgAnswered: number
  sundayAvgAnswered: number
}

export type TPhoneCallReportingData = {
  hasInboundDirectCalls: boolean
  totalCalls: TCountWithChange
  voicemails: TCountWithChange
  missedCalls: TMissedCalls
  answeredRate: TAnsweredRate
  totalInbound: TCountWithChange
  inboundBreakdown: {
    aiAgent: number
    direct: number
  }
  totalOutbound: TCountWithChange
  outboundBreakdown: {
    aiAgent: number
    direct: number
  }
  callsByLocation: TCallsByLocation[]
  callsByLocationTruncated: boolean
  callsByAssistant: TCallsByAssistant[]
  callsByAssistantTruncated: boolean
  callVolumeOverTime: TCallVolumePoint[]
  callMetricsOverTime: TCallMetricsOverTimePoint[]
  callsByHourOfDay: TCallsByHourPoint[]
  callsByDayOfWeek: TCallsByDayOfWeekPoint[]
  answeredByHourAndDayOfWeek: TAnsweredByHourAndDayPoint[]
  avgCallDurationSeconds: number
}

export type TPhoneCallReportingApiResponse = Partial<TPhoneCallReportingData>

export type TReportFilterOptionsAssistant = {
  agentId: string
  name: string
  locationId: string | null
  phoneNumbers: string[]
}

export type TReportFilterOptionsLocation = {
  locationId: string
  name: string
  city: string | null
  state: string | null
  agentIds: string[]
}

export type TReportFilterOptionsPhoneNumber = {
  phoneNumber: string
  digits: string
  label: string
  agentIds: string[]
  locationId: string | null
}

export type TReportFilterOptionsCampaign = {
  campaignId: string
  name: string
  status: string | null
  agentAccessScope: string | null
}

/**
 * Most campaigns one export can filter by. The ids travel in the query string
 * (37 characters each), so "Select all" on a large org would otherwise build a
 * URL near the server's 16 KB header limit and fail with an unhelpful error.
 */
export const MAX_EXPORT_CAMPAIGNS = 100

export type TReportFilterOptions = {
  assistants: TReportFilterOptionsAssistant[]
  locations: TReportFilterOptionsLocation[]
  phoneNumbers: TReportFilterOptionsPhoneNumber[]
  campaigns: TReportFilterOptionsCampaign[]
  hasActiveLocations: boolean
}

export const VOIP_MATCHBACK_SOURCE_TYPE_OPTIONS = [
  {
    key: EVoipMatchbackSourceType.all,
    label: 'All sources',
    value: EVoipMatchbackSourceType.all,
  },
  {
    key: EVoipMatchbackSourceType.web,
    label: 'Web',
    value: EVoipMatchbackSourceType.web,
  },
  {
    key: EVoipMatchbackSourceType.email,
    label: 'Email',
    value: EVoipMatchbackSourceType.email,
  },
  {
    key: EVoipMatchbackSourceType.phone,
    label: 'Phone',
    value: EVoipMatchbackSourceType.phone,
  },
  {
    key: EVoipMatchbackSourceType.chat,
    label: 'Chat',
    value: EVoipMatchbackSourceType.chat,
  },
  {
    key: EVoipMatchbackSourceType.upload,
    label: 'Upload',
    value: EVoipMatchbackSourceType.upload,
  },
  {
    key: EVoipMatchbackSourceType.campaignUpload,
    label: 'Campaign Upload',
    value: EVoipMatchbackSourceType.campaignUpload,
  },
]

export const VOIP_MATCHBACK_LEAD_DETAIL_HEAD_CELLS = [
  { id: 'leadPhoneNumber', label: 'Lead Phone', isSortable: true },
  { id: 'leadName', label: 'Lead Name', isSortable: true },
  { id: 'leadLocation', label: 'Location', isSortable: true },
  { id: 'leadCreatedDate', label: 'Lead Date', isSortable: true },
  { id: 'outboundCalls', label: 'Outbound Call Count', isSortable: true },
  {
    id: 'timeToFirstCallMinutes',
    label: 'Time to First Call',
    isSortable: true,
  },
  {
    id: 'timeToSecondCallMinutes',
    label: 'Time to Second Call',
    isSortable: true,
  },
  { id: 'lastCallDate', label: 'Last Call Date', isSortable: false },
] satisfies THeadCellWithActions<TVoipMatchbackLead & { lastCallDate: string }>

export const PANEL_DIRECTION_OPTIONS = [
  {
    key: ECallDirectionFilterValue.all,
    label: 'All Directions',
    value: ECallDirectionFilterValue.all,
  },
  {
    key: ECallDirectionFilterValue.inbound,
    label: 'Inbound',
    value: ECallDirectionFilterValue.inbound,
  },
  {
    key: ECallDirectionFilterValue.outbound,
    label: 'Outbound',
    value: ECallDirectionFilterValue.outbound,
  },
]

export const PANEL_ORIGINATION_OPTIONS = [
  {
    key: ECallOriginationFilterValue.all,
    label: 'All Types',
    value: ECallOriginationFilterValue.all,
  },
  {
    key: ECallOriginationFilterValue.aiAgent,
    label: 'AI',
    value: ECallOriginationFilterValue.aiAgent,
  },
  {
    key: ECallOriginationFilterValue.direct,
    label: 'Direct',
    value: ECallOriginationFilterValue.direct,
  },
]

export const FILTER_INPUT_HEIGHT = 40
export const ALL_LOCATIONS_VALUE = 'all'
export const ALL_PHONE_NUMBERS_VALUE = 'all'

export type TPhoneCallReportFilterValues = {
  datePreset: string
  customStartDate: string
  customEndDate: string
  agentId: string
  phoneNumber: string
  direction: ECallDirectionFilterValue
  origination: ECallOriginationFilterValue
}

export type TOrgMetricsPhoneSummary = {
  totalPhoneCalls: number
  liveTransfers: number
  liveTransfersPerTotalCalls: number | null
}

export type TOrgMetricsByLocationRow = {
  agentId: string
  locationName: string
  locationPhoneNumber: string | null
  totalPhoneCalls: number
  liveTransfers: number
  inboundPhoneCalls: number
  outboundPhoneCalls: number
  phoneCallsWithLinksSent: number
}

export type TOrgMetricsConversationDirection = {
  inboundConversations: number
  outboundConversations: number
  unspecifiedDirectionConversations: number
}

export type TOrgMetricsTopicRow = {
  topic: string
  conversationCount: number
}

export type TOrgMetricsLiveTransferDestinationRow = {
  liveTransferDestination: string
  transferCount: number
}

export type TOrgMetricsPhoneLinksSummary = {
  phoneCallsWithLinksSent: number
}

export type TOrgMetricsConversationDetailRow = {
  conversationId: string
  /** From `Conversation.agentId`. */
  agentId: string | null
  /** From `Conversation.contactId`. */
  contactId: string | null
  assistantReferenceName: string | null
  createdDateIso: string
  conversationSummary: string | null
  conversationTranscript: string | null
  sentLinks: string | null
  liveTransferDestination: string | null
  channel: string
  campaignId: string | null
  qualification: string | null
  liveTransferName: string | null
  contactFirstName: string | null
  contactLastName: string | null
  contactPhone: string | null
  contactEmail: string | null
  customAnalysisFields: Record<string, unknown> | null
  originType: string | null
  /** `Location.name` via `Conversation.locationId`, falling back to `Agent.locationId`. */
  conversationLocationName: string | null
  /** Outbound when `campaignId` is set, otherwise inbound. */
  conversationDirection: 'inbound' | 'outbound'
  /**
   * VoIP direct-call fields (from `ConversationDirectCall`, one-to-one with Conversation).
   * Only populated for `originType === 'direct'` rows; null for AI-agent / non-VoIP.
   * Voicemail audio file is intentionally omitted.
   */
  directCallId: string | null
  directCallPhoneNumberId: string | null
  directCallSid: string | null
  directCallFromPhoneNumber: string | null
  directCallToPhoneNumber: string | null
  directCallStartedAtIso: string | null
  directCallAnsweredAtIso: string | null
  directCallEndedAtIso: string | null
  directCallDurationSeconds: number | null
  directCallOutcome: string | null
  directCallLeftVoicemail: boolean | null
  directCallVoicemailDurationSeconds: number | null
  directCallCreatedAtIso: string | null
  directCallUpdatedAtIso: string | null
}

export type TOrgMetricsLeadRow = {
  contactId: string
  createdDateIso: string
  name: string | null
  firstName: string | null
  lastName: string | null
  email: string | null
  phone: string | null
  source: string | null
  /** From `Contact.leadSource` — distinct from `Contact.source`. */
  leadSource: string | null
  /** From `Contact.leadCreatedDate`. */
  leadCreatedDateIso: string | null
  status: string | null
  qualification: string | null
  agentId: string | null
  /** `Location.name` via `Agent.locationId`; blank when the agent has no location. */
  locationName: string | null
  assistantReferenceName: string | null
  conversationCount: number
  lastConversationDateIso: string | null
}

export type TOrgMetricsUsageAnalyticsRow = {
  agentId: string
  /** `Location.name` via `Agent.locationId`; blank when the agent has no location. */
  locationName: string | null
  assistantReferenceName: string | null
  agentName: string | null
  totalConversations: number
  inboundConversations: number
  outboundConversations: number
  aiOriginatedConversations: number
  directOriginatedConversations: number
  phoneConversations: number
  emailConversations: number
  textConversations: number
  chatConversations: number
  liveTransfersOccurred: number
  voicemailsLeft: number
  conversationsWithLinks: number
  avgConversationDurationSeconds: number | null
  totalConversationDurationSeconds: number
}

export type TOrgMetricsExportPayload = {
  generatedAtIso: string
  startDateIso: string
  endDateIso: string
  orgId: string
  metricsNotes: readonly string[]
  phoneSummary: TOrgMetricsPhoneSummary
  byLocation: TOrgMetricsByLocationRow[]
  conversationDirection: TOrgMetricsConversationDirection | null
  topTopics: TOrgMetricsTopicRow[]
  phoneLinksSummary: TOrgMetricsPhoneLinksSummary | null
  liveTransferDestinations: TOrgMetricsLiveTransferDestinationRow[]
  conversationDetails: TOrgMetricsConversationDetailRow[]
  leadsData: TOrgMetricsLeadRow[]
  usageAnalytics: TOrgMetricsUsageAnalyticsRow[]
}

export type TOrgMetricsExportApiResponse = {
  data?: TOrgMetricsExportPayload | null
}

export type TCsvFileId =
  | 'conversation-history'
  | 'leads-data'
  | 'usage-analytics'
  | 'voip-conversation-data'
  | 'voip-lead-response'
  | 'campaign-contacts'

export const PDF_OPTION_ID = 'executive-summary'

/** CSVs included in POST /api/reporting/org-metrics-export async zip. */
export type TOrgMetricsAsyncCsvFileId = TCsvFileId

/** Async org-metrics export ZIP section ids (CSVs + optional executive PDF). */
export const ORG_METRICS_ASYNC_CSV_IDS: readonly TOrgMetricsAsyncCsvFileId[] = [
  'conversation-history',
  'leads-data',
  'usage-analytics',
  'voip-conversation-data',
  'voip-lead-response',
  'campaign-contacts',
] as const

/** Section id sent in `sections` when the executive summary PDF is selected. */
export const ORG_METRICS_ASYNC_PDF_SECTION_ID = PDF_OPTION_ID

const ORG_METRICS_ASYNC_CSV_ID_SET = new Set<string>(ORG_METRICS_ASYNC_CSV_IDS)

export function isOrgMetricsAsyncCsvId(
  id: TCsvFileId,
): id is TOrgMetricsAsyncCsvFileId {
  return ORG_METRICS_ASYNC_CSV_ID_SET.has(id)
}

type TBaseExportOption = {
  id: string
  title: string
  description: string
  icon: ReactNode
}

export type TCsvExportOption = TBaseExportOption & {
  format: 'CSV'
  type: 'csv'
  csvId: TCsvFileId
}

export type TPdfExportOption = TBaseExportOption & {
  format: 'PDF'
  type: 'pdf'
}

export type TExportOption = TCsvExportOption | TPdfExportOption

export type TChannelOption = {
  key: string
  label: string
  value: EConversationChannel
}

export const CHANNEL_OPTIONS: TChannelOption[] = [
  { key: 'phone', label: 'Phone', value: EConversationChannel.phone },
  { key: 'email', label: 'Email', value: EConversationChannel.email },
  { key: 'text', label: 'Text', value: EConversationChannel.text },
  { key: 'chatbot', label: 'Chat', value: EConversationChannel.chatbot },
]
